import type { CSSProperties } from 'react';
import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '@/auth/auth-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ApiError,
  apiClient,
  MOCK_SIGNED_OUT_KEY,
  setRefreshToken,
  setToken,
} from '@/lib/api-client';

/** Mock mode (VITE_USE_MOCKS !== 'false') has no real auth backend. */
const useMocks = (import.meta.env.VITE_USE_MOCKS as string | undefined) !== 'false';

/** Payload returned by POST /api/admin/auth/login (envelope already unwrapped). */
interface AdminLoginData {
  accessToken: string;
  refreshToken: string;
  role: string;
  email: string;
}

/**
 * Operator sign-in page.
 *
 * Posts credentials to the JWT-only `POST /api/admin/auth/login` endpoint,
 * stores the returned access token, then does a full navigation to `/fleet`
 * so `AuthProvider` re-reads the token from localStorage on the next load.
 */
export function LoginPage(): JSX.Element {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Already signed in — skip the form.
  if (user) return <Navigate to="/overview" replace />;

  // Mock mode has no auth backend; re-seed the dev admin JWT and re-enter.
  async function continueAsMock(): Promise<void> {
    sessionStorage.removeItem(MOCK_SIGNED_OUT_KEY);
    const { seedMockJwt } = await import('@/mocks/dev-auth');
    seedMockJwt();
    window.location.assign('/overview');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // apiClient unwraps the { success, code, data } envelope.
      const res = await apiClient.post<AdminLoginData>('/admin/auth/login', {
        email,
        password,
      });

      setToken(res.accessToken);
      setRefreshToken(res.refreshToken);
      window.location.assign('/overview');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError('Invalid email or password.');
        else if (err.status === 403) setError('This account does not have operator access.');
        else setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-4">
      {/* Signal glow behind the card */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[120px]"
        aria-hidden
      />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-sm space-y-7 rounded-md border border-border bg-card p-8 shadow-panel"
      >
        {/* Accent hairline along the top edge */}
        <div className="accent-rule absolute inset-x-0 top-0 h-px" aria-hidden />

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span
              className="signal-dot animate-pulse-dot bg-status-active"
              style={{ ['--bg-dot']: 'var(--status-active)' } as CSSProperties}
              aria-hidden
            />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Planet Proxy
            </span>
          </div>
          <div className="space-y-1">
            <h1 className="text-lg font-semibold tracking-tight">Operator sign-in</h1>
            <p className="text-sm text-muted-foreground">Admin and support roles only.</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground"
          >
            Email
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={submitting}
            className="font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="password"
            className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground"
          >
            Password
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={submitting}
            className="font-mono"
          />
        </div>

        {error && (
          <p
            className="rounded-sm border border-status-failed/30 bg-status-failed/[0.08] px-3 py-2 text-sm text-status-failed"
            role="alert"
          >
            {error}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>

        {useMocks && (
          <div className="space-y-2 border-t border-border pt-5">
            <p className="text-center font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              Mock mode — no real backend
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => void continueAsMock()}
            >
              Continue as mock admin
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
